/**
 * useTrailForGoal — returns ONE live trail a saved Bucket List place belongs
 * to (joined trail preferred), or null. Read-only: never joins anything.
 * The stop list and the user's joins/visits are fetched once and shared.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isBucketItem } from "@/lib/bucketPlan";
import { pickTrailHint, type TrailHint, type TrailStopRef } from "@/lib/trailHints";
import type { Goal } from "@/types/todo";

const FIVE_MIN = 5 * 60 * 1000;

function useTrailStops() {
  return useQuery({
    queryKey: ["trail-hint-stops"],
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<TrailStopRef[]> => {
      const { data, error } = await supabase
        .from("discovery_stops")
        .select("id,name,goal_id,map_places!discovery_stops_map_place_id_fkey(name),common_goals(title,end_date,image_url)");
      if (error) throw error;
      return (data ?? [])
        .filter((r: any) => r.common_goals)
        .map((r: any) => ({
          stopId: r.id,
          stopName: r.name,
          placeName: r.map_places?.name ?? null,
          trailId: r.goal_id,
          trailTitle: r.common_goals.title,
          trailImage: r.common_goals.image_url ?? null,
          endDate: r.common_goals.end_date,
        }));
    },
  });
}

export function useMyTrailActivity() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["trail-hint-activity", user?.id],
    enabled: !!user,
    staleTime: FIVE_MIN,
    queryFn: async () => {
      const [joins, visits] = await Promise.all([
        supabase.from("common_goal_participants").select("common_goal_id").eq("user_id", user!.id),
        supabase.from("discovery_visits" as any).select("stop_id").eq("user_id", user!.id),
      ]);
      return {
        joined: new Set((joins.data ?? []).map((j: any) => j.common_goal_id as string)),
        visited: new Set(((visits.data as any[]) ?? []).map((v) => v.stop_id as string)),
      };
    },
  });
}

export function useTrailForGoal(goal?: Goal | null): { hint: TrailHint | null; visited: boolean } {
  const bucket = !!goal && isBucketItem(goal);
  const { data: stops } = useTrailStops();
  const { data: activity } = useMyTrailActivity();
  if (!bucket || !stops) return { hint: null, visited: false };
  const hint = pickTrailHint(goal!.title, stops, activity?.joined ?? new Set());
  return { hint, visited: !!hint && !!activity?.visited.has(hint.stopId) };
}
